/* ============================================================
   SEBUYA — Modern interactions
   - Scroll fade-in (IntersectionObserver)
   - Header glassmorphism + shrink on scroll
   - Floating back-to-top with circular progress ring
   - Live purchase ticker (slide-in notifications)
   - Count-up animated stats
   - Free shipping progress bar (live cart updates)
   ============================================================ */
(function () {
  'use strict';

  // ---------- 1. SCROLL FADE-IN ----------
  // Trigger ~600px BEFORE entering viewport so the fade is done by the time
  // the user actually scrolls there — avoids the "section pops in" feel.
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('sx-in');
          observer.unobserve(e.target);
        }
      });
    },
    { rootMargin: '600px 0px 600px 0px', threshold: 0 }
  );
  function bindReveals() {
    document.querySelectorAll('.sx-reveal:not(.sx-in)').forEach((el) => observer.observe(el));
  }

  // ---------- 2. HEADER SCROLL EFFECTS (throttled via rAF) ----------
  const header = document.querySelector('.sx-h');
  let lastY = 0;
  let scrollScheduled = false;
  function doScroll() {
    const y = window.scrollY;
    if (header) {
      header.classList.toggle('sx-h--scrolled', y > 30);
      header.classList.toggle('sx-h--hide', y > 200 && y > lastY + 4);
      if (y < lastY - 4) header.classList.remove('sx-h--hide');
    }
    lastY = y;
    updateBackToTop(y);
    scrollScheduled = false;
  }
  function onScroll() {
    if (scrollScheduled) return;
    scrollScheduled = true;
    requestAnimationFrame(doScroll);
  }

  // ---------- 3. BACK TO TOP WITH PROGRESS RING ----------
  const backTop = document.querySelector('.sx-backtop');
  const ring = backTop?.querySelector('circle.sx-backtop__ring');
  const ringLength = ring ? 2 * Math.PI * (ring.r?.baseVal?.value || 20) : 0;
  if (ring) {
    ring.style.strokeDasharray = ringLength;
    ring.style.strokeDashoffset = ringLength;
  }
  function updateBackToTop(y) {
    if (!backTop) return;
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docH > 0 ? Math.min(1, y / docH) : 0;
    backTop.classList.toggle('sx-backtop--show', y > 400);
    if (ring) ring.style.strokeDashoffset = ringLength * (1 - pct);
  }
  backTop?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));

  // ---------- 5. COUNT-UP ANIMATED STATS ----------
  const counters = document.querySelectorAll('[data-count]');
  const countObs = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const el = e.target;
      const target = parseInt(el.dataset.count, 10) || 0;
      const dur = 1400;
      const start = performance.now();
      function tick(now) {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        el.textContent = Math.round(eased * target).toLocaleString('de-DE');
        if (t < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
      countObs.unobserve(el);
    });
  }, { threshold: 0.4 });
  counters.forEach((c) => countObs.observe(c));

  // ---------- 6. FREE SHIPPING PROGRESS BAR ----------
  const shipBar = document.querySelector('.sx-ship[data-threshold]');
  if (shipBar) {
    const threshold = parseFloat(shipBar.dataset.threshold || '50');
    const cur = shipBar.querySelector('[data-ship-cur]');
    const left = shipBar.querySelector('[data-ship-left]');
    const fill = shipBar.querySelector('.sx-ship__fill');
    const msg  = shipBar.querySelector('.sx-ship__msg');

    function fmt(n) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
    }
    function render(total) {
      const pct = Math.min(100, (total / threshold) * 100);
      const remaining = Math.max(0, threshold - total);
      if (fill) fill.style.width = pct + '%';
      if (cur)  cur.textContent = fmt(total);
      if (left) left.textContent = fmt(remaining);
      if (msg) {
        if (remaining <= 0) {
          msg.innerHTML = '🎉 <strong>Glückwunsch!</strong> Dein Versand ist <strong>kostenlos</strong>.';
          shipBar.classList.add('sx-ship--done');
        } else {
          msg.innerHTML = 'Noch <strong data-ship-left>' + fmt(remaining) + '</strong> für <strong>kostenlosen Versand</strong>';
          shipBar.classList.remove('sx-ship--done');
        }
      }
    }

    function refresh() {
      fetch('/cart.js', { headers: { Accept: 'application/json' }})
        .then((r) => r.json())
        .then((data) => render((data.total_price || 0) / 100))
        .catch(() => {});
    }
    refresh();
    document.addEventListener('cart:refresh', refresh);
    // Refresh after add-to-cart form submit
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) setTimeout(refresh, 800);
    });
  }

  // ---------- 7. ROTATING SEARCH PLACEHOLDER (pause when offscreen + tab hidden) ----------
  function initSearchRotate() {
    const input = document.querySelector('.sx-h-search input[data-rotate]');
    if (!input) return;
    // Skip on touch devices (placeholder rarely seen, and users tap to focus immediately)
    if (window.matchMedia('(hover: none)').matches) return;
    const items = (input.dataset.rotate || '').split('|').filter(Boolean);
    if (!items.length) return;
    let i = 0, typed = '', task = null, paused = false;
    function type() {
      if (paused) return;
      const target = items[i % items.length];
      if (typed.length < target.length) {
        typed = target.slice(0, typed.length + 1);
        input.placeholder = typed + '▌';
        task = setTimeout(type, 55 + Math.random() * 35);
      } else {
        input.placeholder = typed;
        task = setTimeout(erase, 1800);
      }
    }
    function erase() {
      if (paused) return;
      if (typed.length > 0) {
        typed = typed.slice(0, -1);
        input.placeholder = typed + '▌';
        task = setTimeout(erase, 30);
      } else {
        i++;
        task = setTimeout(type, 350);
      }
    }
    function start() { paused = false; if (!task && document.activeElement !== input) type(); }
    function stop() { paused = true; clearTimeout(task); task = null; }
    input.addEventListener('focus', () => { stop(); input.placeholder = 'Suche…'; });
    input.addEventListener('blur', () => setTimeout(start, 800));
    document.addEventListener('visibilitychange', () => { document.hidden ? stop() : start(); });
    start();
  }

  // ---------- 8. CATEGORY TILE 3D TILT (desktop only, rAF-throttled) ----------
  function initTilt() {
    if (window.matchMedia('(hover: none)').matches) return; // skip touch devices
    if (window.matchMedia('(max-width: 989px)').matches) return; // skip mobile/tablet
    document.querySelectorAll('.sx-cartoon').forEach((el) => {
      let scheduled = false;
      let pendingRx = 0, pendingRy = 0;
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        pendingRx = (0.5 - (e.clientY - r.top) / r.height) * 8;
        pendingRy = ((e.clientX - r.left) / r.width - 0.5) * 10;
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          el.style.setProperty('--rx', pendingRx + 'deg');
          el.style.setProperty('--ry', pendingRy + 'deg');
          scheduled = false;
        });
      });
      el.addEventListener('mouseleave', () => {
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      });
    });
  }

  // ---------- 9. AJAX ADD-TO-CART + CART DRAWER ----------
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const FREE_SHIP_THRESHOLD = 5000; // €50.00 in cents — gilt nur für Lieferungen nach Deutschland
  const SX_FREE_DE = !(window.Shopify && window.Shopify.country && window.Shopify.country !== 'DE');
  const SX_AUSLAND_MSG = '🚚 Versandkosten ins Ausland: <a href="/policies/shipping-policy" style="color:inherit;text-decoration:underline">Versandinfo</a>';

  function formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function cartDrawerOpen() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('sx-cart--open');
    document.documentElement.classList.add('sx-no-scroll');
    fetchCart();
  }
  function cartDrawerClose() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('sx-cart--open');
    document.documentElement.classList.remove('sx-no-scroll');
  }

  function renderCart(cart) {
    if (!cartDrawer) return;
    const itemsBox = cartDrawer.querySelector('[data-cart-items]');
    const emptyBox = cartDrawer.querySelector('[data-cart-empty]');
    const foot = cartDrawer.querySelector('[data-cart-foot]');
    const countEl = cartDrawer.querySelector('[data-cart-count]');
    const subEl = cartDrawer.querySelector('[data-cart-subtotal]');
    const totalEl = cartDrawer.querySelector('[data-cart-total]');
    const shipMsg = cartDrawer.querySelector('[data-cart-ship-msg]');
    const shipFill = cartDrawer.querySelector('[data-cart-ship-fill]');

    if (countEl) countEl.textContent = cart.item_count || 0;

    if (!cart.items || cart.items.length === 0) {
      itemsBox.hidden = true;
      emptyBox.hidden = false;
      foot.hidden = true;
      if (shipFill) shipFill.style.width = '0%';
      if (shipMsg) shipMsg.innerHTML = SX_FREE_DE ? '🚚 <strong>€50,00</strong> für kostenlosen Versand (DE)' : SX_AUSLAND_MSG;
      return;
    }

    emptyBox.hidden = true;
    itemsBox.hidden = false;
    foot.hidden = false;

    // Subtotal + total
    if (subEl) subEl.textContent = formatMoney(cart.items_subtotal_price || cart.total_price);
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

    // Free shipping progress
    const remaining = Math.max(0, FREE_SHIP_THRESHOLD - cart.total_price);
    const pct = Math.min(100, (cart.total_price / FREE_SHIP_THRESHOLD) * 100);
    if (shipFill) { shipFill.style.width = pct + '%'; if (!SX_FREE_DE && shipFill.parentNode) shipFill.parentNode.style.display = 'none'; }
    if (shipMsg && !SX_FREE_DE) {
      shipMsg.innerHTML = SX_AUSLAND_MSG;
    } else if (shipMsg) {
      if (remaining === 0) {
        shipMsg.innerHTML = '✓ <strong>Kostenloser Versand</strong> freigeschaltet 🎉';
      } else {
        shipMsg.innerHTML = '🚚 Nur noch <strong>' + formatMoney(remaining) + '</strong> für kostenlosen Versand';
      }
    }

    // Items
    itemsBox.innerHTML = cart.items.map(function(item) {
      const img = item.image ? item.image.replace(/(\.\w+)(\?.*)?$/, '_120x$1$2') : '';
      const compare = item.original_price > item.final_price
        ? '<s>' + formatMoney(item.original_price * item.quantity) + '</s> '
        : '';
      const variant = (item.variant_options || []).filter(function(v) { return v && v !== 'Default Title'; }).join(' · ');
      return '\
<div class="sx-cart-item" data-key="' + item.key + '">\
  <a class="sx-cart-item__media" href="' + item.url + '">' + (img ? '<img src="' + img + '" alt="' + (item.title||'').replace(/"/g,'&quot;') + '" loading="lazy">' : '<span>🛒</span>') + '</a>\
  <div class="sx-cart-item__body">\
    ' + (item.vendor ? '<span class="sx-cart-item__brand">' + item.vendor + '</span>' : '') + '\
    <a class="sx-cart-item__title" href="' + item.url + '">' + item.product_title + '</a>\
    ' + (variant ? '<span class="sx-cart-item__variant">' + variant + '</span>' : '') + '\
    <div class="sx-cart-item__bottom">\
      <div class="sx-cart-item__qty">\
        <button type="button" data-cart-qty="-1" aria-label="Eins weniger">−</button>\
        <input type="number" value="' + item.quantity + '" min="0" max="99" data-cart-qty-input>\
        <button type="button" data-cart-qty="1" aria-label="Eins mehr">+</button>\
      </div>\
      <div class="sx-cart-item__price">' + compare + '<strong>' + formatMoney(item.final_line_price) + '</strong></div>\
    </div>\
  </div>\
  <button class="sx-cart-item__remove" data-cart-remove="' + item.key + '" type="button" aria-label="Entfernen">×</button>\
</div>';
    }).join('');
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) { renderCart(cart); return cart; })
      .catch(function() {});
  }

  function updateCartItem(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function(r) { return r.json(); })
      .then(function(cart) { renderCart(cart); return cart; });
  }

  if (cartDrawer) {
    // Close handlers
    cartDrawer.querySelectorAll('[data-cart-close]').forEach(function(el) {
      el.addEventListener('click', cartDrawerClose);
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && cartDrawer.classList.contains('sx-cart--open')) cartDrawerClose();
    });

    // Header cart icon → open drawer instead of going to /cart
    document.addEventListener('click', function(e) {
      const cartLink = e.target.closest('.sx-h-side__item--cart, [href="/cart"]:not([data-cart-skip-drawer])');
      if (cartLink && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        cartDrawerOpen();
      }
    });

    // Qty + remove inside drawer
    cartDrawer.addEventListener('click', function(e) {
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) { updateCartItem(remove.getAttribute('data-cart-remove'), 0); return; }
      const btn = e.target.closest('[data-cart-qty]');
      if (btn) {
        const row = btn.closest('.sx-cart-item');
        const input = row.querySelector('[data-cart-qty-input]');
        const delta = parseInt(btn.getAttribute('data-cart-qty'));
        const newQty = Math.max(0, parseInt(input.value || '0') + delta);
        updateCartItem(row.getAttribute('data-key'), newQty);
      }
    });
    cartDrawer.addEventListener('change', function(e) {
      const input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      const row = input.closest('.sx-cart-item');
      const qty = Math.max(0, parseInt(input.value || '0'));
      updateCartItem(row.getAttribute('data-key'), qty);
    });
  }

  // Legacy toast still available as fallback when drawer missing
  function showCartToast(productName) {
    if (cartDrawer) { cartDrawerOpen(); return; }
    let toast = document.querySelector('.sx-cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'sx-cart-toast';
      toast.innerHTML = '<span class="sx-cart-toast__check">✓</span><span class="sx-cart-toast__msg"></span><a class="sx-cart-toast__link" href="/cart">Warenkorb →</a>';
      document.body.appendChild(toast);
    }
    toast.querySelector('.sx-cart-toast__msg').textContent = productName + ' wurde hinzugefügt';
    toast.classList.add('sx-cart-toast--show');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function() { toast.classList.remove('sx-cart-toast--show'); }, 3200);
  }
  // Pure DOM update (no fetch) — cart_data from /cart/add.js response carries cart info if needed.
  // For accurate total + free-ship bar we still need /cart.js but defer it to be non-blocking.
  function bumpCartCount(addedQty) {
    const cartA = document.querySelector('.sx-h-side__item--cart');
    let countEl = cartA?.querySelector('.sx-h-side__count');
    const prev = countEl ? parseInt(countEl.textContent || '0') : 0;
    const next = prev + (addedQty || 1);
    if (!countEl && next > 0 && cartA) {
      countEl = document.createElement('span');
      countEl.className = 'sx-h-side__count';
      cartA.appendChild(countEl);
    }
    if (countEl) {
      countEl.textContent = next;
      countEl.classList.remove('sx-bounce');
      void countEl.offsetWidth;
      countEl.classList.add('sx-bounce');
    }
    const mc = document.querySelector('.sebuya-mobile-bar__count');
    if (mc) mc.textContent = next;
    // Defer free-ship bar refresh — non-blocking, after animations settle
    if (window.requestIdleCallback) {
      requestIdleCallback(() => document.dispatchEvent(new CustomEvent('cart:refresh')), { timeout: 800 });
    } else {
      setTimeout(() => document.dispatchEvent(new CustomEvent('cart:refresh')), 400);
    }
  }
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-cart-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('.sx-card__btn');
    const titleEl = form.closest('.sx-card-wrap')?.querySelector('.sx-card__title');
    const productName = titleEl?.textContent.trim() || 'Produkt';
    if (btn) btn.classList.add('sx-card__btn--loading');
    const fd = new FormData(form);
    const qty = parseInt(fd.get('quantity') || '1');
    fetch(form.action, {
      method: 'POST',
      body: fd,
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && (data.id || data.product_id)) {
          if (btn) {
            btn.classList.remove('sx-card__btn--loading');
            btn.classList.add('sx-card__btn--done');
            setTimeout(() => btn.classList.remove('sx-card__btn--done'), 2000);
          }
          // Pass added qty so we update count locally without fetch
          bumpCartCount(qty);
          showCartToast(productName);
        } else {
          if (btn) btn.classList.remove('sx-card__btn--loading');
          window.location.href = '/cart';
        }
      })
      .catch(() => {
        if (btn) btn.classList.remove('sx-card__btn--loading');
        window.location.href = '/cart';
      });
  });

  // ---------- 10. WISHLIST (localStorage) ----------
  const WL_KEY = 'sebuya_wishlist';
  function wlGet() { try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch(e) { return []; } }
  function wlSet(arr) { try { localStorage.setItem(WL_KEY, JSON.stringify(arr)); } catch(e) {} }
  function wlUpdateCount() {
    const count = wlGet().length;
    // Header Liste link
    const liste = document.querySelector('.sx-h-side__item[aria-label="Wunschliste"]');
    if (liste) {
      let badge = liste.querySelector('.sx-h-side__count');
      if (!badge && count > 0) {
        badge = document.createElement('span');
        badge.className = 'sx-h-side__count sx-h-side__count--wl';
        liste.appendChild(badge);
      }
      if (badge) {
        if (count > 0) {
          badge.textContent = count;
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      }
    }
  }
  function wlPaintHearts() {
    const ids = wlGet();
    document.querySelectorAll('.sx-card__fav').forEach((btn) => {
      const wrap = btn.closest('.sx-card-wrap');
      const link = wrap?.querySelector('.sx-card__media-link, .sx-card__body-link');
      const handle = link?.getAttribute('href')?.match(/\/products\/([^/?#]+)/)?.[1];
      if (!handle) return;
      btn.dataset.handle = handle;
      const isFav = ids.includes(handle);
      btn.classList.toggle('sx-card__fav--on', isFav);
      btn.textContent = isFav ? '♥' : '♡';
      btn.setAttribute('aria-pressed', isFav ? 'true' : 'false');
    });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.sx-card__fav');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.handle;
    if (!handle) return;
    let ids = wlGet();
    const wasOn = ids.includes(handle);
    if (wasOn) {
      ids = ids.filter((h) => h !== handle);
      btn.classList.remove('sx-card__fav--on');
      btn.textContent = '♡';
    } else {
      ids.unshift(handle);
      btn.classList.add('sx-card__fav--on', 'sx-card__fav--pop');
      btn.textContent = '♥';
      setTimeout(() => btn.classList.remove('sx-card__fav--pop'), 500);
      // Floating heart particles (2 only — was 4)
      const r = btn.getBoundingClientRect();
      for (let i = 0; i < 2; i++) {
        const p = document.createElement('span');
        p.className = 'sx-fav-particle';
        p.textContent = '♥';
        p.style.left = r.left + r.width / 2 + (i ? 10 : -10) + 'px';
        p.style.top = r.top + r.height / 2 + 'px';
        p.style.animationDelay = (i * 80) + 'ms';
        document.body.appendChild(p);
        setTimeout(() => p.remove(), 900);
      }
    }
    btn.setAttribute('aria-pressed', wasOn ? 'false' : 'true');
    wlSet(ids);
    wlUpdateCount();
  });

  // ---------- 11. WISHLIST DRAWER ----------
  const wlDrawer = document.querySelector('[data-wl-drawer]');
  const wlList = document.querySelector('[data-wl-list]');
  const wlCountText = document.querySelector('[data-wl-count-text]');

  function wlOpenDrawer() {
    if (!wlDrawer) return;
    wlDrawer.classList.add('sx-wl--open');
    document.documentElement.classList.add('sx-no-scroll');
    wlRender();
  }
  function wlCloseDrawer() {
    if (!wlDrawer) return;
    wlDrawer.classList.remove('sx-wl--open');
    document.documentElement.classList.remove('sx-no-scroll');
  }
  function wlRender() {
    if (!wlList) return;
    const ids = wlGet();
    if (wlCountText) wlCountText.textContent = ids.length + (ids.length === 1 ? ' Produkt' : ' Produkte');
    if (!ids.length) {
      wlList.innerHTML = '<div class="sx-wl__empty"><span class="sx-wl__empty-icon">♡</span><h4>Noch keine Lieblinge</h4><p>Tippe auf das Herz auf einem Produkt um es hier zu speichern.</p></div>';
      return;
    }
    wlList.innerHTML = '<div class="sx-wl__skeleton"></div>'.repeat(Math.min(ids.length, 3));
    Promise.all(ids.map((h) => fetch('/products/' + h + '.js').then((r) => r.ok ? r.json() : null).catch(() => null)))
      .then((products) => {
        wlList.innerHTML = products.filter(Boolean).map((p) => {
          const v = p.variants && (p.variants.find((x) => x.available) || p.variants[0]); // 29.09: tükenmiş tekli yerine satılabilir varyant (koli-tek ürünler)
          const price = v ? (v.price / 100).toFixed(2) : '';
          const compare = v && v.compare_at_price && v.compare_at_price > v.price ? (v.compare_at_price / 100).toFixed(2) : '';
          const img = p.featured_image || (p.images && p.images[0]) || '';
          return '\
<div class="sx-wl-item" data-h="' + p.handle + '">\
  <a class="sx-wl-item__media" href="/products/' + p.handle + '">' + (img ? '<img src="' + img + '" alt="' + (p.title||'').replace(/"/g,'&quot;') + '">' : '<div class="sx-wl-item__ph">🛒</div>') + '</a>\
  <div class="sx-wl-item__body">\
    ' + (p.vendor ? '<span class="sx-wl-item__brand">' + p.vendor + '</span>' : '') + '\
    <a class="sx-wl-item__title" href="/products/' + p.handle + '">' + p.title + '</a>\
    <div class="sx-wl-item__price">' + (compare ? '<s>€' + compare + '</s> ' : '') + '<strong>€' + price + '</strong></div>\
  </div>\
  <div class="sx-wl-item__actions">\
    <button class="sx-wl-item__remove" data-wl-remove="' + p.handle + '" aria-label="Entfernen">×</button>\
    ' + (v ? '<form class="sx-wl-item__add" data-cart-add action="/cart/add" method="post"><input type="hidden" name="id" value="' + v.id + '"><input type="hidden" name="quantity" value="1"><button type="submit">+ Sepete</button></form>' : '') + '\
  </div>\
</div>';
        }).join('');
      });
  }

  document.addEventListener('click', (e) => {
    // Open drawer on Liste click
    const liste = e.target.closest('.sx-h-side__item[aria-label="Wunschliste"]');
    if (liste) {
      e.preventDefault();
      wlOpenDrawer();
      return;
    }
    // Close drawer
    if (e.target.closest('[data-wl-close]')) { wlCloseDrawer(); return; }
    // Remove from wishlist (inside drawer)
    const rm = e.target.closest('[data-wl-remove]');
    if (rm) {
      const h = rm.getAttribute('data-wl-remove');
      const ids = wlGet().filter((x) => x !== h);
      wlSet(ids);
      wlUpdateCount();
      wlPaintHearts();
      wlRender();
    }
  });

  // ---------- 12. QUICK VIEW MODAL ----------
  const qv = document.querySelector('[data-qv]');
  const qvBody = document.querySelector('[data-qv-body]');

  function qvOpen() { qv?.classList.add('sx-qv--open'); document.documentElement.classList.add('sx-no-scroll'); }
  function qvClose() { qv?.classList.remove('sx-qv--open'); document.documentElement.classList.remove('sx-no-scroll'); }

  function qvShow(handle) {
    if (!qv || !qvBody) return;
    qvBody.innerHTML = '<div class="sx-qv__loading"><div class="sx-qv__spinner"></div><span>Laden…</span></div>';
    qvOpen();
    fetch('/products/' + handle + '.js')
      .then((r) => r.json())
      .then((p) => {
        const v = p.variants && (p.variants.find((x) => x.available) || p.variants[0]); // 29.09: tükenmiş tekli yerine satılabilir varyant (koli-tek ürünler)
        const price = v ? (v.price / 100).toFixed(2) : '';
        const compare = v && v.compare_at_price && v.compare_at_price > v.price ? (v.compare_at_price / 100).toFixed(2) : '';
        const pct = compare ? Math.round((compare - price) * 100 / compare) : 0;
        const img = p.featured_image || (p.images && p.images[0]) || '';
        const desc = (p.description || '').replace(/<[^>]+>/g, ' ').slice(0, 240);
        const variantOpts = (p.variants || []).length > 1
          ? '<div class="sx-qv__variants"><label>Variante</label><select name="id">' + p.variants.map((vv) => '<option value="' + vv.id + '"' + (vv.available ? '' : ' disabled') + '>' + vv.title + ' — €' + (vv.price/100).toFixed(2) + (vv.available ? '' : ' (ausverk.)') + '</option>').join('') + '</select></div>'
          : (v ? '<input type="hidden" name="id" value="' + v.id + '">' : '');
        qvBody.innerHTML = '\
<div class="sx-qv-grid">\
  <div class="sx-qv-grid__media">\
    ' + (pct > 0 ? '<span class="sx-qv__badge">-' + pct + '%</span>' : '') + '\
    ' + (img ? '<img src="' + img + '" alt="' + (p.title||'').replace(/"/g,'&quot;') + '">' : '<div class="sx-qv__ph">🛒</div>') + '\
  </div>\
  <div class="sx-qv-grid__info">\
    ' + (p.vendor ? '<span class="sx-qv__brand">' + p.vendor + '</span>' : '') + '\
    <h2 class="sx-qv__title">' + p.title + '</h2>\
    <div class="sx-qv__price">' + (compare ? '<s>€' + compare + '</s>' : '') + '<strong>€' + price + '</strong></div>\
    ' + (desc ? '<p class="sx-qv__desc">' + desc + (p.description && p.description.length > 240 ? '…' : '') + '</p>' : '') + '\
    <form class="sx-qv__form" data-cart-add action="/cart/add" method="post">\
      ' + variantOpts + '\
      <input type="hidden" name="quantity" value="1">\
      <div class="sx-qv__cta-row">\
        <button type="submit" class="sx-qv__add"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><circle cx="9" cy="21" r="1.4"/><circle cx="19" cy="21" r="1.4"/><path d="M2.5 3h3l2.6 12.4a2 2 0 0 0 2 1.6h9.2a2 2 0 0 0 2-1.6L23 7H7"/></svg> In den Warenkorb</button>\
        <a href="/products/' + p.handle + '" class="sx-qv__more">Vollständige Details →</a>\
      </div>\
    </form>\
  </div>\
</div>';
      })
      .catch(() => {
        qvBody.innerHTML = '<div class="sx-qv__error">Produkt konnte nicht geladen werden. <a href="/products/' + handle + '">Zur Produktseite</a></div>';
      });
  }

  document.addEventListener('click', (e) => {
    const trg = e.target.closest('[data-qv-handle]');
    if (trg) {
      e.preventDefault();
      e.stopPropagation();
      qvShow(trg.getAttribute('data-qv-handle'));
      return;
    }
    if (e.target.closest('[data-qv-close]')) qvClose();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { qvClose(); wlCloseDrawer(); }
  });

  // ---------- 13. RECENTLY VIEWED (peek panel + tracking) ----------
  const RV_KEY = 'sebuya_recent';
  const RV_MAX = 5;
  function rvGet() { try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch(e) { return []; } }
  function rvSet(arr) { try { localStorage.setItem(RV_KEY, JSON.stringify(arr.slice(0, RV_MAX))); } catch(e) {} }
  function rvTrack() {
    const m = location.pathname.match(/^\/products\/([^/?#]+)/);
    if (!m) return;
    const h = m[1];
    let ids = rvGet().filter((x) => x !== h);
    ids.unshift(h);
    rvSet(ids);
  }
  const rvEl = document.querySelector('[data-rv]');
  const rvList = document.querySelector('[data-rv-list]');
  function rvRender() {
    if (!rvEl || !rvList) return;
    // Skip on product page itself
    if (location.pathname.startsWith('/products/')) return;
    const ids = rvGet();
    if (!ids.length || sessionStorage.getItem('sx_rv_dismissed') === '1') { rvEl.classList.remove('sx-rv--show'); return; }
    Promise.all(ids.slice(0, 4).map((h) => fetch('/products/' + h + '.js').then((r) => r.ok ? r.json() : null).catch(() => null)))
      .then((products) => {
        const valid = products.filter(Boolean);
        if (!valid.length) return;
        rvList.innerHTML = valid.map((p) => {
          const img = p.featured_image || (p.images && p.images[0]) || '';
          const rv = p.variants && (p.variants.find((x) => x.available) || p.variants[0]); // 29.09: satılabilir varyant
          const price = rv ? (rv.price / 100).toFixed(2) : '';
          return '\
<a class="sx-rv-item" href="/products/' + p.handle + '">\
  ' + (img ? '<img src="' + img + '" alt="">' : '<span class="sx-rv-item__ph">🛒</span>') + '\
  <span class="sx-rv-item__name">' + p.title.slice(0, 22) + (p.title.length > 22 ? '…' : '') + '</span>\
  <span class="sx-rv-item__price">€' + price + '</span>\
</a>';
        }).join('');
        // Show after a delay (so it doesn't pop immediately on page load)
        setTimeout(() => rvEl.classList.add('sx-rv--show'), 5000);
      });
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-rv-close]')) {
      sessionStorage.setItem('sx_rv_dismissed', '1');
      rvEl?.classList.remove('sx-rv--show');
    }
  });
  rvTrack();

  // ---------- 14. UNIFIED COUNTDOWN MANAGER ----------
  // Single tick for ALL [data-end] elements + IntersectionObserver
  // + document.hidden check + slower interval on mobile.
  // Replaces per-section setInterval (was 3 simultaneous timers)
  function initCountdowns() {
    const elements = document.querySelectorAll('[data-end]');
    if (!elements.length) return;
    const visible = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) visible.add(e.target);
        else visible.delete(e.target);
      });
    }, { rootMargin: '200px' });
    elements.forEach((el) => {
      io.observe(el);
      const end = new Date(el.getAttribute('data-end')).getTime();
      if (isNaN(end)) return;
      el.__sxEnd = end;
    });
    function pad(n) { return String(n).padStart(2, '0'); }
    function tick() {
      if (document.hidden) return;
      const now = Date.now();
      visible.forEach((el) => {
        const end = el.__sxEnd;
        if (!end) return;
        const diff = end - now;
        if (diff <= 0) {
          ['[data-d]','[data-h]','[data-m]','[data-s]'].forEach((sel) => {
            const sp = el.querySelector(sel);
            if (sp) sp.textContent = '00';
          });
          return;
        }
        const dd = Math.floor(diff / 86400000);
        const hh = Math.floor((diff % 86400000) / 3600000);
        const mm = Math.floor((diff % 3600000) / 60000);
        const ss = Math.floor((diff % 60000) / 1000);
        const d = el.querySelector('[data-d]'), h = el.querySelector('[data-h]'),
              m = el.querySelector('[data-m]'), s = el.querySelector('[data-s]');
        if (d) d.textContent = pad(dd);
        if (h) h.textContent = pad(hh);
        if (m) m.textContent = pad(mm);
        if (s) s.textContent = pad(ss);
      });
    }
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const interval = isMobile ? 5000 : 1000; // throttle 5x on mobile
    tick();
    setInterval(tick, interval);
    // Pause/resume on tab visibility change (cheap)
    document.addEventListener('visibilitychange', () => { if (!document.hidden) tick(); });
  }

  // ---------- 15. CATEGORY SCROLL INDICATOR (mobile hint dismiss) ----------
  function initCatScrollHint() {
    const grid = document.querySelector('.sx-cartoon-grid');
    if (!grid) return;
    const parentSection = grid.closest('.sx-section--tight');
    if (!parentSection) return;
    let dismissed = false;
    function onScroll() {
      if (dismissed) return;
      if (grid.scrollLeft > 30) {
        parentSection.classList.add('sx-cats-scrolled');
        dismissed = true;
        grid.removeEventListener('scroll', onScroll);
      }
    }
    grid.addEventListener('scroll', onScroll, { passive: true });
  }

  // ---------- INIT ----------
  document.addEventListener('DOMContentLoaded', () => {
    bindReveals();
    initSearchRotate();
    initTilt();
    wlPaintHearts();
    wlUpdateCount();
    rvRender();
    initCountdowns();
    initCatScrollHint();
    initCookieBanner();
    initNewsletterPopup();
  });
  bindReveals();
  initSearchRotate();
  initTilt();
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // ---------- 16. COOKIE CONSENT (GDPR) ----------
  // Einwilligung: localStorage (Bannerzustand) + Shopify Customer Privacy API + Google Consent Mode.
  // Widerruf jederzeit über [data-cookie-reopen] (Footer „Cookie-Einstellungen“), DSGVO Art. 7(3).
  // Gültigkeit 12 Monate, danach wird erneut gefragt.
  function initCookieBanner() {
    const banner = document.querySelector('[data-cookie-banner]');
    if (!banner) return;
    const panel = banner.querySelector('[data-cookie-panel]');
    const KEY = 'sebuya_cookie_consent_v1';
    const MAX_AGE = 365 * 24 * 60 * 60 * 1000;

    function read() {
      try {
        const c = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (c && (!c.ts || Date.now() - c.ts > MAX_AGE)) { localStorage.removeItem(KEY); return null; }
        return c;
      } catch (e) { return null; }
    }

    function withPrivacy(fn) {
      if (window.Shopify && Shopify.customerPrivacy) { fn(Shopify.customerPrivacy); return; }
      if (window.Shopify && Shopify.loadFeatures) {
        Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function(err) {
          if (err) { console.warn('[Sebuya] Customer Privacy API nicht geladen', err); return; }
          fn(Shopify.customerPrivacy);
        });
      }
    }

    function toShopify(consent) {
      withPrivacy(function(P) {
        try {
          P.setTrackingConsent({
            analytics: !!consent.analytics,
            marketing: !!consent.marketing,
            preferences: !!consent.analytics,
            sale_of_data: !!consent.marketing
          }, function() {});
        } catch (e) { console.warn('[Sebuya] setTrackingConsent fehlgeschlagen', e); }
      });
    }

    function toGtag(consent) {
      if (typeof window.gtag !== 'function') return;
      window.gtag('consent', 'update', {
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data: consent.marketing ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
        analytics_storage: consent.analytics ? 'granted' : 'denied'
      });
    }

    function apply(consent) {
      const c = { analytics: !!consent.analytics, marketing: !!consent.marketing };
      try { localStorage.setItem(KEY, JSON.stringify(Object.assign({}, c, { ts: Date.now() }))); } catch (e) {}
      toShopify(c);
      toGtag(c);
      if (panel) panel.hidden = true;
      banner.hidden = true;
    }

    function open(withPanel) {
      const cur = read();
      const a = banner.querySelector('[data-cookie-cat="analytics"]');
      const m = banner.querySelector('[data-cookie-cat="marketing"]');
      if (a) a.checked = !!(cur && cur.analytics);
      if (m) m.checked = !!(cur && cur.marketing);
      banner.hidden = false;
      if (panel) panel.hidden = !withPanel;
    }

    const stored = read();
    if (!stored) {
      setTimeout(function() { open(false); }, 600);
    } else {
      // Abgleich: hat Shopify keine Entscheidung mehr gespeichert (Cookie gelöscht),
      // die gültige gespeicherte Wahl erneut übermitteln.
      withPrivacy(function(P) {
        try {
          const cur = P.currentVisitorConsent ? P.currentVisitorConsent() : null;
          if (cur && cur.analytics === '' && cur.marketing === '') toShopify(stored);
        } catch (e) { console.warn('[Sebuya] Consent-Abgleich fehlgeschlagen', e); }
      });
    }

    banner.querySelector('[data-cookie-accept]')?.addEventListener('click', function() {
      apply({ analytics: true, marketing: true });
    });
    banner.querySelector('[data-cookie-decline]')?.addEventListener('click', function() {
      apply({ analytics: false, marketing: false });
    });
    banner.querySelector('[data-cookie-config]')?.addEventListener('click', function() {
      if (panel) panel.hidden = false;
    });
    banner.querySelector('[data-cookie-panel-close]')?.addEventListener('click', function() {
      if (panel) panel.hidden = true;
    });
    banner.querySelector('[data-cookie-save]')?.addEventListener('click', function() {
      apply({
        analytics: banner.querySelector('[data-cookie-cat="analytics"]').checked,
        marketing: banner.querySelector('[data-cookie-cat="marketing"]').checked
      });
    });
    document.querySelectorAll('[data-cookie-reopen]').forEach(function(el) {
      el.addEventListener('click', function(e) { e.preventDefault(); open(true); });
    });
  }

  // ---------- 17. NEWSLETTER POPUP (10% off) ----------
  function initNewsletterPopup() {
    const popup = document.querySelector('[data-nl-popup]');
    if (!popup) return;
    const KEY = 'sebuya_nl_popup_v1';
    const shown = localStorage.getItem(KEY);

    // Don't show if: already shown, on checkout, on account pages, customer already logged in
    if (shown) return;
    if (/\/checkout|\/account|\/cart/.test(location.pathname)) return;
    if (document.body.classList.contains('customer-logged-in')) return;

    let triggered = false;
    function showPopup() {
      if (triggered) return;
      triggered = true;
      popup.hidden = false;
      requestAnimationFrame(function() { popup.classList.add('sx-nlpop--open'); });
      localStorage.setItem(KEY, String(Date.now()));
    }
    function close() {
      popup.classList.remove('sx-nlpop--open');
      setTimeout(function() { popup.hidden = true; }, 320);
    }

    // Trigger 1: 22 seconds on page
    setTimeout(showPopup, 22000);

    // Trigger 2: exit intent (mouse leaves top of viewport)
    document.addEventListener('mouseout', function(e) {
      if (e.clientY <= 0 && !e.relatedTarget && !triggered) showPopup();
    });

    // Trigger 3: scroll depth > 60%
    let scrollTriggered = false;
    window.addEventListener('scroll', function() {
      if (scrollTriggered || triggered) return;
      const scrolled = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (scrolled > 0.6) {
        scrollTriggered = true;
        setTimeout(showPopup, 1200);
      }
    }, { passive: true });

    popup.querySelectorAll('[data-nl-close]').forEach(function(el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && popup.classList.contains('sx-nlpop--open')) close();
    });
  }
})();
